import { useTranslation } from 'react-i18next';
import { ScrollView, View } from 'react-native';
import {
  COLOR,
  FONT_SIZE,
  FONT_WEIGHT,
  RADIUS,
  SPACING,
} from 'constants/design-system';
import { DateTime } from 'luxon';

import { ReprintMasterLabelButton } from 'modules/ChefScene/components/ActionButton';
import { OrderItems } from 'modules/GridView/components/OrderDetails/OrderItems';
import {
  AttendedList,
  UnattendedList,
} from 'modules/OverviewScene/NotificationsLog';
import { useAppSelector } from 'store/hooks';
import { OrderPreparationStatuses, OrderStatuses } from 'store/Orders/enums';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { useFormatDateTime } from 'util/helpers';
import { BaseCard } from 'components/BaseCard';
import { BaseText } from 'components/BaseText';
import CancelledLabel from 'components/CancelledLabel';

import { CardHeaderSection } from './components/CardHeaderSection';
import { FinancialInfoCard } from './components/FinancialInfoCard';
import { Header } from './components/Header';
import { OrderItemDetail } from './components/OrderItemDetail';
import type { BaseCardProps } from 'components/BaseCard';
import type { Order, OrderItem } from 'store/Orders/types';
import type { OrderDetailCardState } from './OrderDetailCardStateContainer';

export type OrderDetailCardProps = Pick<BaseCardProps, 'style'> & {
  order: Order;
  items: OrderItem[];
  isCommentExpand: boolean;
  isFinancialDetailVisible: boolean;
  isNotificationVisible: boolean;
  selectedOrderItemUuid: string | undefined;
  onPressOrderItem: (itemUuid: string) => void;
  isAllHeaderOptionVisible: boolean;
  isCancelledOverride?: boolean;
  itemOverride?: OrderItem;
  onPressNotificationUnattendedList?: () => void;
  onPressNotificationAttendedList?: () => void;
};

export const OrderDetailCard = (props: OrderDetailCardProps) => {
  const {
    items,
    order,
    style,
    isCommentExpand,
    isFinancialDetailVisible,
    isNotificationVisible,
    onPressOrderItem,
    selectedOrderItemUuid,
    isAllHeaderOptionVisible,
    isCancelledOverride,
    itemOverride,
    onPressNotificationUnattendedList,
    onPressNotificationAttendedList,
  } = props;

  const formatDateTime = useFormatDateTime();
  const featureFlags = useAppSelector(UserSelectors.selectFeatureFlags);

  const { t } = useTranslation();
  const interfaceSettingsFeatureFlag = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );

  const { shouldDisplayAllActionButton } = useAppSelector(
    SettingSelectors.selectActionButtonsInterfaceSettings,
  );

  const isCancelled =
    order.status === OrderPreparationStatuses.CANCELLED ||
    order.order_status === OrderStatuses.CANCELLED ||
    isCancelledOverride;

  return (
    <View style={style}>
      {order.scheduled_for && (
        <View
          style={{
            backgroundColor: COLOR.theme.blue[400],
            marginHorizontal: SPACING.theme[10],
            borderTopLeftRadius: RADIUS.theme.md,
            borderTopRightRadius: RADIUS.theme.md,
            padding: SPACING.theme[2],
          }}
        >
          <BaseText
            color={COLOR.theme.white}
            textAlign="center"
            fontWeight={FONT_WEIGHT.theme.bold}
            fontSize={FONT_SIZE.theme.sm}
          >
            {`${t('overview.orderDetails.scheduledFor').toUpperCase()} ${formatDateTime(order.scheduled_for, DateTime.TIME_SIMPLE)} - ${formatDateTime(
              order.scheduled_for,
              {
                month: 'short',
                day: 'numeric',
              },
            )}`}
          </BaseText>
        </View>
      )}
      <BaseCard
        style={[
          {
            padding: SPACING.theme[6],
            flexDirection: 'row',
            backgroundColor: isCancelled
              ? COLOR.theme.gray[500]
              : COLOR.system.orderKinds[order.kind],
            gap: SPACING.theme[8],
            flexGrow: 1,
            flexShrink: 1,
          },
          order.scheduled_for
            ? {
                borderWidth: 3,
                borderColor: COLOR.theme.blue[400],
              }
            : false,
        ]}
      >
        <BaseCard
          style={{ padding: 0, backgroundColor: COLOR.theme.white, flex: 1 }}
        >
          {selectedOrderItemUuid ? (
            <OrderItemDetail
              itemUuid={selectedOrderItemUuid}
              itemOverride={itemOverride}
              order={order}
              items={items}
            />
          ) : (
            <OrderItems
              actionButtonArrangement={'vertical'}
              order={order}
              items={items}
              onPressItem={onPressOrderItem}
            />
          )}
        </BaseCard>
        <View style={{ flex: 1, gap: SPACING.theme[8] }}>
          {!isCommentExpand && (
            <Header
              isAllHeaderOptionVisible={isAllHeaderOptionVisible}
              order={order}
              items={items}
              selectedOrderItemUuid={selectedOrderItemUuid}
            />
          )}

          <ScrollView
            contentContainerStyle={{
              flexShrink: 1,
              gap: SPACING.theme[4],
            }}
          >
            {!isCommentExpand && (
              <>
                {isFinancialDetailVisible &&
                  featureFlags.display_financial_info && (
                    <FinancialInfoCard financialInfo={order.financial_info} />
                  )}
                {isNotificationVisible && (
                  <>
                    {/* TODO: update this component design */}
                    <UnattendedList
                      onPress={onPressNotificationUnattendedList}
                      orderUUID={selectedOrderItemUuid ? undefined : order.uuid}
                      items={
                        selectedOrderItemUuid
                          ? items.filter(
                              (item) => item.uuid === selectedOrderItemUuid,
                            )
                          : items
                      }
                      style={{ borderRadius: 4, maxHeight: 120 }}
                    />
                    {/* TODO: update this component design */}
                    <AttendedList
                      onPress={onPressNotificationAttendedList}
                      items={
                        selectedOrderItemUuid
                          ? items.filter(
                              (item) => item.uuid === selectedOrderItemUuid,
                            )
                          : items
                      }
                      orderUUID={selectedOrderItemUuid ? undefined : order.uuid}
                      style={{ borderRadius: 4, maxHeight: 120 }}
                    />
                  </>
                )}
              </>
            )}
            <View
              style={{
                flexDirection: 'row',
                gap: SPACING.theme[4],
                flexShrink: 1,
                flexGrow: 1,
              }}
            >
              {order.special_instructions && (
                <View
                  style={{ flex: 1, gap: SPACING.theme[4], minHeight: 120 }}
                >
                  <BaseCard
                    style={{
                      backgroundColor: COLOR.theme.white,
                      flex: 1,
                      gap: SPACING.theme[6],
                    }}
                    scrollable
                  >
                    <CardHeaderSection
                      title={t(
                        'chef.itemDetails.specialInstructions.title',
                      ).toUpperCase()}
                      color={COLOR.theme.red[500]}
                      icon={{
                        variant: 'material-community',
                        name: 'account-details',
                      }}
                    />
                    <BaseText
                      fontSize={FONT_SIZE.theme.sm}
                      color={COLOR.theme.red[500]}
                    >
                      {order.special_instructions}
                    </BaseText>
                  </BaseCard>
                </View>
              )}
              {order.internal_notes && order.internal_notes.length > 0 && (
                <View
                  style={{ flex: 1, gap: SPACING.theme[4], minHeight: 120 }}
                >
                  <BaseCard
                    style={{
                      backgroundColor: COLOR.theme.white,
                      flex: 1,
                    }}
                    contentContainerStyle={{
                      gap: SPACING.theme[6],
                    }}
                    scrollable
                  >
                    <CardHeaderSection
                      title={t(
                        'chef.itemDetails.orderComments.title',
                      ).toUpperCase()}
                      icon={{
                        variant: 'material',
                        name: 'headset-mic',
                      }}
                    />

                    {order.internal_notes?.map((internalNote, index) => {
                      return (
                        <BaseText fontSize={FONT_SIZE.theme.sm} key={index}>
                          {internalNote.note}
                        </BaseText>
                      );
                    })}
                  </BaseCard>
                </View>
              )}
            </View>
          </ScrollView>

          {!interfaceSettingsFeatureFlag.action_button_order
            .reprint_master_label ||
          !shouldDisplayAllActionButton ||
          isCommentExpand ||
          selectedOrderItemUuid ||
          isCancelled ? null : (
            <View
              style={{
                marginTop: 'auto',
                gap: SPACING.theme[4],
              }}
            >
              <ReprintMasterLabelButton
                style={{ width: '100%' }}
                orderUUID={order.uuid}
              />
            </View>
          )}
        </View>
      </BaseCard>
      {isCancelled && <CancelledLabel />}
    </View>
  );
};

type OrderDetailCardWithStateContainerProps = Pick<
  OrderDetailCardProps,
  | 'items'
  | 'order'
  | 'style'
  | 'isCancelledOverride'
  | 'itemOverride'
  | 'onPressNotificationAttendedList'
  | 'onPressNotificationUnattendedList'
> &
  Pick<
    OrderDetailCardState,
    | 'isCommentExpand'
    | 'isFinancialDetailVisible'
    | 'isNotificationVisible'
    | 'selectedOrderItemUuid'
    | 'setSelectedOrderItemUuid'
    | 'isAllHeaderOptionVisible'
  >;

export const OrderDetailCardForStateContainer = (
  props: OrderDetailCardWithStateContainerProps,
) => {
  const {
    isCommentExpand,
    isFinancialDetailVisible,
    isNotificationVisible,
    selectedOrderItemUuid,
    setSelectedOrderItemUuid,
    items,
    order,
    isCancelledOverride,
    style,
    itemOverride,
    isAllHeaderOptionVisible,
    onPressNotificationUnattendedList,
    onPressNotificationAttendedList,
  } = props;
  return (
    <OrderDetailCard
      onPressNotificationUnattendedList={onPressNotificationUnattendedList}
      onPressNotificationAttendedList={onPressNotificationAttendedList}
      isCancelledOverride={isCancelledOverride}
      style={style}
      itemOverride={itemOverride}
      items={items}
      isCommentExpand={isCommentExpand}
      isFinancialDetailVisible={isFinancialDetailVisible}
      isNotificationVisible={isNotificationVisible}
      onPressOrderItem={(itemUuid) => {
        if (itemUuid) {
          setSelectedOrderItemUuid(itemUuid);
        }
      }}
      order={order}
      selectedOrderItemUuid={selectedOrderItemUuid}
      isAllHeaderOptionVisible={isAllHeaderOptionVisible}
    />
  );
};
